import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import ClienteLayout from '../../components/ClienteLayout'
import { useAuth } from '../../lib/AuthContext'
import { supabase } from '../../lib/supabase'
import {
  getMisSuscripciones, getDashboardResumen, getKitsConProductos,
  pausarSuscripcion, reactivarSuscripcion, cancelarMiSuscripcion,
} from '../../lib/portalClienteApi'

const ESTADO_PILL = {
  activa:    { color: '#16a34a', label: 'Activa' },
  pausada:   { color: '#f59e0b', label: 'Pausada' },
  cancelada: { color: '#ef4444', label: 'Cancelada' },
}

const TIER_BADGE = {
  essential: { bg: '#e6fafc', color: '#0f7480', label: 'Línea Essential' },
  premium:   { bg: '#fef3c7', color: '#92400e', label: 'Línea Premium'   },
  comercial: { bg: '#ede9fe', color: '#5b21b6', label: 'Línea Comercial' },
}

function fmtFechaCorta(iso) {
  if (!iso) return '—'
  const d = new Date(iso + (iso.length === 10 ? 'T12:00:00' : ''))
  const dias = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb']
  const meses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
  return `${dias[d.getDay()]} ${d.getDate()} ${meses[d.getMonth()]}`
}

function fmtFechaLarga(iso) {
  if (!iso) return '—'
  const d = new Date(iso + (iso.length === 10 ? 'T12:00:00' : ''))
  const dias = ['Domingo','Lunes','Martes','Miércoles','Jueves','Viernes','Sábado']
  const meses = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre']
  return `${dias[d.getDay()]} ${d.getDate()} de ${meses[d.getMonth()]}`
}

function fmtMonto(n) {
  return '$' + Number(n || 0).toLocaleString('es-UY', { maximumFractionDigits: 0 })
}

function diaSemanaLabel(d) {
  const map = { lunes: 'Lun', martes: 'Mar', miercoles: 'Mié', jueves: 'Jue', viernes: 'Vie', sabado: 'Sáb', domingo: 'Dom' }
  return map[d] || d
}

// Emoji placeholder según nombre del producto (mientras no haya foto cargada)
function emojiPorNombre(nombre = '') {
  const n = nombre.toLowerCase()
  if (n.includes('papel higiénico') || n.includes('higienico')) return '🧻'
  if (n.includes('papel') || n.includes('toalla')) return '🧻'
  if (n.includes('jabón ropa') || n.includes('jabón de ropa') || n.includes('lavarropa')) return '🧺'
  if (n.includes('2-en-1') || n.includes('suavizante')) return '🧺'
  if (n.includes('lavavajillas') || n.includes('detergente')) return '🍽️'
  if (n.includes('limpiapisos') || n.includes('piso')) return '🪣'
  if (n.includes('desengrasante')) return '🍳'
  if (n.includes('esponja')) return '🧽'
  if (n.includes('vidrio')) return '🪟'
  if (n.includes('bolsa')) return '🗑️'
  if (n.includes('manos')) return '🧼'
  if (n.includes('lavandina') || n.includes('cloro')) return '🧴'
  if (n.includes('desinfectante') || n.includes('limpiador')) return '🧴'
  if (n.includes('perfume') || n.includes('ambiente')) return '🌸'
  return '🧴'
}

export default function MiPlan() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [suscripciones, setSuscripciones] = useState([])
  const [resumen, setResumen] = useState(null)
  const [kitProductos, setKitProductos] = useState([])
  const [productosFotos, setProductosFotos] = useState({})  // sku → imagen_url
  const [cargando, setCargando] = useState(true)
  const [accion, setAccion] = useState(null)  // null | 'pausar' | 'cancelar'

  useEffect(() => { if (user?.id) cargar() }, [user?.id])

  async function cargar() {
    setCargando(true)
    const ss = await getMisSuscripciones(user.id)
    setSuscripciones(ss)
    if (ss[0]) {
      const [r, kits] = await Promise.all([
        getDashboardResumen(ss[0].id),
        getKitsConProductos(),
      ])
      setResumen(r)
      const miKit = kits.find(k => k.codigo === ss[0].kit_actual)
      setKitProductos(miKit?.productos || [])

      // Traer imagen_url de los productos del kit
      const skus = (miKit?.productos || []).map(p => p.sku)
      if (skus.length > 0) {
        const { data } = await supabase
          .from('productos')
          .select('sku, imagen_url')
          .in('sku', skus)
        const map = {}
        ;(data || []).forEach(p => { if (p.imagen_url) map[p.sku] = p.imagen_url })
        setProductosFotos(map)
      }
    }
    setCargando(false)
  }

  const susc = suscripciones[0]
  const tieneSusc = !!susc
  const estado = susc?.estado || 'activa'
  const estadoInfo = ESTADO_PILL[estado] || ESTADO_PILL.activa
  const proximaFecha = resumen?.proximaEntregaReal || resumen?.proximaEntregaCalc
  const proximoPago = resumen?.proximoPago

  async function handlePausar() {
    if (!confirm('¿Pausar suscripción 1 mes? No vamos a hacer entregas ni cobrarte hasta que la reactives.')) return
    try {
      await pausarSuscripcion(susc.id, 'Pausa solicitada por cliente')
      cargar()
    } catch (e) { alert('Error al pausar: ' + e.message) }
  }

  async function handleReactivar() {
    try {
      await reactivarSuscripcion(susc.id)
      cargar()
    } catch (e) { alert('Error al reactivar: ' + e.message) }
  }

  async function handleCancelar() {
    if (!confirm('¿Cancelar la suscripción definitivamente? Podés volver cuando quieras.')) return
    try {
      await cancelarMiSuscripcion(susc.id, 'Cancelado por cliente desde portal')
      navigate('/cliente')
    } catch (e) { alert('Error al cancelar: ' + e.message) }
  }

  return (
    <ClienteLayout title="Mi Plan">
      <div className="mp-wrap">
        <div className="mp-headline">
          <h1>Mi Plan</h1>
          <p>Tu suscripción actual y todo lo que viene en tus próximas entregas.</p>
        </div>

        {cargando ? (
          <div className="mp-loading">Cargando…</div>
        ) : !tieneSusc ? (
          <div className="mp-empty">
            <div className="mp-empty-icon">📦</div>
            <h2>Todavía no tenés un plan activo</h2>
            <p>Armá tu primera suscripción y empezá a ahorrar 10% abajo del súper, sin permanencia.</p>
            <Link to="/cliente/nueva-suscripcion" className="mp-empty-cta">Empezar ahora →</Link>
          </div>
        ) : (
          <>
            {/* HERO del kit */}
            <div className="mp-hero-grid">
              <div className="mp-hero">
                <div className="mp-hero-pills">
                  <span className="mp-hero-tag" style={{ color: estadoInfo.color }}>● {estadoInfo.label}</span>
                  {susc.kit?.tier && TIER_BADGE[susc.kit.tier] && (
                    <span className="mp-hero-tier" style={{ background: TIER_BADGE[susc.kit.tier].bg, color: TIER_BADGE[susc.kit.tier].color }}>
                      {TIER_BADGE[susc.kit.tier].label}
                    </span>
                  )}
                </div>
                <h2 className="mp-hero-h">{susc.kit?.nombre || 'Tu Kit'}</h2>
                <div className="mp-hero-sub">
                  {susc.alias && <strong>{susc.alias} · </strong>}
                  {susc.barrio || ''}
                  {susc.direccion ? ` · ${susc.direccion}` : ''}
                  {susc.dia_entrega ? ` · entrega ${diaSemanaLabel(susc.dia_entrega)}` : ''}
                </div>
                <div className="mp-hero-row">
                  <div className="mp-hero-cell">
                    <div className="mp-hero-cell-label">Cuota mensual</div>
                    <div className="mp-hero-cell-val">{fmtMonto(susc.kit?.precio_susc || proximoPago?.monto)}</div>
                  </div>
                  <div className="mp-hero-cell">
                    <div className="mp-hero-cell-label">Día de cobro</div>
                    <div className="mp-hero-cell-val">{susc.dia_cobro_mes ? `${susc.dia_cobro_mes} de cada mes` : '—'}</div>
                  </div>
                  <div className="mp-hero-cell">
                    <div className="mp-hero-cell-label">Productos</div>
                    <div className="mp-hero-cell-val">{kitProductos.length}</div>
                  </div>
                </div>
              </div>

              {/* Acciones */}
              <div className="mp-actions">
                <Link to="/cliente/tienda" className="mp-action">
                  <div className="mp-action-icon">🛒</div>
                  <div className="mp-action-label">Sumar productos</div>
                </Link>
                <Link to="/cliente/tienda#kits-section" className="mp-action">
                  <div className="mp-action-icon">✏️</div>
                  <div className="mp-action-label">Cambiar mi kit</div>
                </Link>
                {estado === 'pausada' ? (
                  <button className="mp-action" onClick={handleReactivar}>
                    <div className="mp-action-icon">▶️</div>
                    <div className="mp-action-label">Reactivar</div>
                  </button>
                ) : (
                  <button className="mp-action" onClick={handlePausar}>
                    <div className="mp-action-icon">⏸️</div>
                    <div className="mp-action-label">Pausar 1 mes</div>
                  </button>
                )}
                <button className="mp-action danger" onClick={handleCancelar}>
                  <div className="mp-action-icon">✕</div>
                  <div className="mp-action-label">Cancelar</div>
                </button>
              </div>
            </div>

            {/* Próxima entrega + lista productos */}
            <div className="mp-section-title">
              📦 Próxima entrega · {fmtFechaCorta(proximaFecha)}
            </div>
            <div className="mp-grid">
              <div className="mp-kit-list">
                {kitProductos.length === 0 ? (
                  <div className="mp-kit-empty">No hay productos cargados en este kit todavía.</div>
                ) : kitProductos.map(p => (
                  <div className={`mp-kit-row${p.es_regalo ? ' is-regalo' : ''}`} key={p.sku}>
                    <div className="mp-kit-img">
                      {productosFotos[p.sku] ? (
                        <img src={productosFotos[p.sku]} alt="" />
                      ) : (
                        <span className="mp-kit-placeholder">{emojiPorNombre(p.nombre)}</span>
                      )}
                    </div>
                    <div className="mp-kit-body">
                      <div className="mp-kit-name">
                        {p.nombre}
                        {p.es_regalo && (
                          <span style={{
                            marginLeft: 8, fontSize: 10, fontWeight: 800, padding: '2px 8px',
                            background: 'linear-gradient(135deg, #FBBF24, #F59E0B)', color: '#fff',
                            borderRadius: 999, letterSpacing: '.06em', textTransform: 'uppercase',
                            verticalAlign: 'middle',
                          }}>🎁 De regalo</span>
                        )}
                      </div>
                      <div className="mp-kit-meta">{p.presentacion}{p.marca ? ` · ${p.marca}` : ''}</div>
                    </div>
                    <div className="mp-kit-qty">×{p.cantidad}</div>
                  </div>
                ))}
              </div>

              {/* Calendario próximas */}
              <div className="mp-cal">
                <h3 className="mp-cal-title">Próximas entregas</h3>
                {proximaFecha && (
                  <div className="mp-cal-item">
                    <div className="mp-cal-date">
                      <div className="mp-cal-day">{new Date(proximaFecha + 'T12:00:00').getDate()}</div>
                      <div className="mp-cal-mes">{['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'][new Date(proximaFecha + 'T12:00:00').getMonth()]}</div>
                    </div>
                    <div className="mp-cal-info">
                      <div className="mp-cal-h">Próxima · {susc.kit_nombre}</div>
                      <div className="mp-cal-sub">{fmtFechaLarga(proximaFecha)}</div>
                    </div>
                    <span className="mp-cal-badge mp-badge-confirmed">Confirmada</span>
                  </div>
                )}
                {proximoPago?.vence && (
                  <div className="mp-cal-item">
                    <div className="mp-cal-date future">
                      <div className="mp-cal-day">{new Date(proximoPago.vence + 'T12:00:00').getDate()}</div>
                      <div className="mp-cal-mes">{['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'][new Date(proximoPago.vence + 'T12:00:00').getMonth()]}</div>
                    </div>
                    <div className="mp-cal-info">
                      <div className="mp-cal-h">Próximo cobro</div>
                      <div className="mp-cal-sub">{fmtMonto(proximoPago.monto)} · vence {fmtFechaCorta(proximoPago.vence)}</div>
                    </div>
                    <span className="mp-cal-badge mp-badge-warn">Pendiente</span>
                  </div>
                )}
              </div>
            </div>

            {/* Otras suscripciones del usuario */}
            {suscripciones.length > 1 && (
              <>
                <div className="mp-section-title">📦 Tus otras suscripciones ({suscripciones.length - 1})</div>
                <div className="mp-otras-grid">
                  {suscripciones.slice(1).map(s => (
                    <div className="mp-otra" key={s.id}>
                      <div className="mp-otra-tag" style={{ color: ESTADO_PILL[s.estado]?.color || '#64748b' }}>● {ESTADO_PILL[s.estado]?.label || s.estado}</div>
                      <div className="mp-otra-h">{s.kit?.nombre || 'Kit'}</div>
                      <div className="mp-otra-sub">
                        {s.alias && <strong>{s.alias} · </strong>}
                        {s.barrio || s.direccion}
                        {s.dia_entrega && ` · ${diaSemanaLabel(s.dia_entrega)}`}
                      </div>
                      <div className="mp-otra-precio">{fmtMonto(s.kit?.precio_susc)}/mes</div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* CTA: agregar otra suscripción (regalar / segunda casa) */}
            <Link to="/cliente/nueva-suscripcion" className="mp-add-susc">
              <div className="mp-add-susc-icon">＋</div>
              <div className="mp-add-susc-body">
                <div className="mp-add-susc-h">Agregar otra suscripción</div>
                <div className="mp-add-susc-sub">¿Querés sumar otro hogar? Una segunda dirección o regalársela a tu mamá, hijo, oficina… vos pagás, ellos reciben.</div>
              </div>
              <div className="mp-add-susc-arrow">›</div>
            </Link>
          </>
        )}
      </div>

      <style>{`
        .mp-wrap { max-width: 1320px; margin: 0 auto; padding: 16px 16px 32px }
        .mp-headline h1 { font-size: 26px; font-weight: 900; color: var(--cl-navy); letter-spacing: -.02em }
        .mp-headline p { font-size: 14px; color: var(--cl-muted); margin-top: 4px; line-height: 1.5 }

        .mp-loading { padding: 40px; text-align: center; color: var(--cl-muted) }
        .mp-empty { background: var(--cl-card); border: 1px solid var(--cl-line); border-radius: 16px; padding: 40px 24px; text-align: center; margin-top: 24px }
        .mp-empty-icon { font-size: 48px; margin-bottom: 12px }
        .mp-empty h2 { font-size: 20px; font-weight: 800; color: var(--cl-navy); letter-spacing: -.01em }
        .mp-empty p { font-size: 14px; color: var(--cl-muted); margin: 8px 0 18px; line-height: 1.5 }
        .mp-empty-cta { display: inline-block; background: linear-gradient(135deg, var(--cl-blue), var(--cl-teal)); color: #fff; padding: 12px 22px; border-radius: 999px; font-weight: 800; font-size: 14px; text-decoration: none }

        .mp-hero-grid { display: grid; grid-template-columns: 1fr; gap: 14px; margin-top: 18px }
        .mp-hero { background: var(--cl-card); border-radius: 18px; padding: 22px; border: 1px solid var(--cl-line) }
        .mp-hero-pills { display: flex; align-items: center; gap: 8px; flex-wrap: wrap }
        .mp-hero-tag { font-size: 10px; font-weight: 800; letter-spacing: .1em; text-transform: uppercase }
        .mp-hero-tier { font-size: 10px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; padding: 4px 10px; border-radius: 999px }
        .mp-hero-h { font-size: 24px; font-weight: 900; color: var(--cl-navy); letter-spacing: -.02em; margin-top: 6px; line-height: 1.2 }
        .mp-hero-sub { font-size: 13px; color: var(--cl-muted); margin-top: 4px }
        .mp-hero-row { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; margin-top: 18px; padding-top: 18px; border-top: 1px solid var(--cl-line2) }
        .mp-hero-cell-label { font-size: 10px; color: var(--cl-muted2); font-weight: 800; letter-spacing: .08em; text-transform: uppercase }
        .mp-hero-cell-val { font-size: 14px; font-weight: 800; color: var(--cl-navy); margin-top: 4px }

        .mp-actions { display: grid; grid-template-columns: 1fr 1fr; gap: 10px }
        .mp-action {
          background: var(--cl-card); border: 1.5px solid var(--cl-line);
          padding: 14px; border-radius: 12px;
          display: flex; align-items: center; gap: 10px;
          cursor: pointer; font-family: inherit;
          text-decoration: none; color: var(--cl-navy);
          transition: border-color .15s, transform .15s;
        }
        .mp-action:hover { border-color: var(--cl-teal); transform: translateY(-1px) }
        .mp-action.danger { border-color: #fecaca; color: #b91c1c }
        .mp-action.danger:hover { border-color: #b91c1c }
        .mp-action-icon { font-size: 20px }
        .mp-action-label { font-size: 13px; font-weight: 800 }

        .mp-section-title { font-size: 16px; font-weight: 900; color: var(--cl-navy); margin: 26px 0 12px }

        .mp-grid { display: grid; grid-template-columns: 1fr; gap: 14px }

        .mp-kit-list { background: var(--cl-card); border-radius: 14px; border: 1px solid var(--cl-line); overflow: hidden }
        .mp-kit-empty { padding: 24px; text-align: center; color: var(--cl-muted); font-size: 13px }
        .mp-kit-row { display: flex; align-items: center; gap: 12px; padding: 12px 14px; border-bottom: 1px solid var(--cl-line2) }
        .mp-kit-row:last-child { border-bottom: 0 }
        .mp-kit-img { width: 44px; height: 54px; border-radius: 8px; flex-shrink: 0; overflow: hidden; background: linear-gradient(135deg, #e3d4be, #d4c5b1); display: flex; align-items: center; justify-content: center }
        .mp-kit-img img { width: 100%; height: 100%; object-fit: cover; display: block }
        .mp-kit-placeholder { font-size: 20px; opacity: .5 }
        .mp-kit-body { flex: 1; min-width: 0 }
        .mp-kit-name { font-size: 13px; font-weight: 700; color: var(--cl-navy); line-height: 1.3 }
        .mp-kit-meta { font-size: 11px; color: var(--cl-muted); margin-top: 2px }
        .mp-kit-qty { background: var(--cl-line2); border-radius: 8px; padding: 4px 10px; font-size: 12px; font-weight: 800; color: var(--cl-navy); flex-shrink: 0 }

        .mp-cal { display: flex; flex-direction: column; gap: 10px }
        .mp-cal-title { font-size: 13px; font-weight: 800; color: var(--cl-navy); margin-bottom: 4px; letter-spacing: .02em }
        .mp-cal-item { background: var(--cl-card); border: 1px solid var(--cl-line); border-radius: 14px; padding: 14px; display: flex; align-items: center; gap: 12px }
        .mp-cal-date { background: var(--cl-navy); color: #fff; width: 50px; height: 50px; border-radius: 10px; flex-shrink: 0; display: flex; flex-direction: column; align-items: center; justify-content: center }
        .mp-cal-date.future { background: var(--cl-muted2) }
        .mp-cal-day { font-size: 18px; font-weight: 900; line-height: 1 }
        .mp-cal-mes { font-size: 9px; opacity: .85; text-transform: uppercase; letter-spacing: .06em; margin-top: 2px }
        .mp-cal-info { flex: 1; min-width: 0 }
        .mp-cal-h { font-size: 13px; font-weight: 800; color: var(--cl-navy) }
        .mp-cal-sub { font-size: 11px; color: var(--cl-muted); margin-top: 2px }
        .mp-cal-badge { font-size: 9px; font-weight: 800; padding: 4px 9px; border-radius: 999px; letter-spacing: .04em; text-transform: uppercase; flex-shrink: 0 }
        .mp-badge-confirmed { background: #e6fafc; color: #0f7480 }
        .mp-badge-warn { background: #fef3c7; color: #92400e }

        .mp-otras-grid { display: grid; grid-template-columns: 1fr; gap: 10px; margin-bottom: 18px }
        .mp-otra {
          background: var(--cl-card); border: 1px solid var(--cl-line);
          border-radius: 14px; padding: 16px;
        }
        .mp-otra-tag { font-size: 10px; font-weight: 800; letter-spacing: .1em; text-transform: uppercase }
        .mp-otra-h { font-size: 17px; font-weight: 900; color: var(--cl-navy); margin-top: 4px; letter-spacing: -.01em }
        .mp-otra-sub { font-size: 12px; color: var(--cl-muted); margin-top: 4px; line-height: 1.45 }
        .mp-otra-precio { font-size: 14px; font-weight: 800; color: var(--cl-navy); margin-top: 8px }

        .mp-add-susc {
          margin-top: 18px;
          background: linear-gradient(135deg, #f0fdf4, #ecfdf5);
          border: 1.5px dashed #86efac; border-radius: 16px;
          padding: 18px; display: flex; align-items: center; gap: 14px;
          text-decoration: none; color: var(--cl-navy);
          transition: transform .15s, border-color .15s;
        }
        .mp-add-susc:hover { transform: translateY(-2px); border-color: #16a34a; border-style: solid }
        .mp-add-susc-icon {
          width: 44px; height: 44px; border-radius: 12px;
          background: #16a34a; color: #fff;
          display: flex; align-items: center; justify-content: center;
          font-size: 26px; font-weight: 300; flex-shrink: 0;
        }
        .mp-add-susc-body { flex: 1; min-width: 0 }
        .mp-add-susc-h { font-size: 14px; font-weight: 800; color: #065f46 }
        .mp-add-susc-sub { font-size: 12.5px; color: #047857; margin-top: 4px; line-height: 1.45 }
        .mp-add-susc-arrow { color: #16a34a; font-size: 22px; font-weight: 800 }

        @media (min-width: 700px) {
          .mp-otras-grid { grid-template-columns: 1fr 1fr }
        }

        @media (min-width: 900px) {
          .mp-wrap { padding: 36px 44px }
          .mp-headline h1 { font-size: 32px }
          .mp-hero-grid { grid-template-columns: 1.4fr 1fr; gap: 20px; margin-top: 24px }
          .mp-hero { padding: 26px }
          .mp-hero-h { font-size: 28px }
          .mp-actions { grid-template-columns: 1fr 1fr; gap: 12px; align-content: start }
          .mp-action { padding: 16px }
          .mp-section-title { font-size: 18px; margin: 32px 0 14px }
          .mp-grid { grid-template-columns: 1.4fr 1fr; gap: 24px }
          .mp-kit-row { padding: 14px 18px; gap: 14px }
          .mp-kit-img { width: 50px; height: 60px }
          .mp-kit-name { font-size: 14px }
          .mp-kit-meta { font-size: 12px }
        }
      `}</style>
    </ClienteLayout>
  )
}
